/** One CPU preparation path shared by the atelier and gameplay actors.
 * The returned arrays are reusable source geometry: callers must not mutate them.
 * The caller owns albedo and must close it only after every required GPU upload. */
import { loadGlb } from '../gltf';
import { buildSkeleton, computeSkin, type Skeleton, type V3 } from '../rig';
import { LANDMARKS } from '../landmarks';
import { fitFace, textureFacePatches, type FaceFit } from '../faceFit';
import type { Body } from '../garments';
import type { Donor } from '../heads';
import { weldedSmoothNormals } from './normals';
import {atelierBaseModel} from '../../../shared/atelier-species';
import {speciesWarp} from '../species';

export interface AtelierModelDefinition { id: string; url: string }
export interface PreparedAtelierModel {
  readonly modelId: string;
  readonly body: Body;
  readonly donor: Donor;
  readonly fit: FaceFit | null;
  readonly skinUv: [number, number, number, number];
  readonly albedo: ImageBitmap;
}

/** Face-space attribute (u, v, frontness, depth); others get frontness −1. */
const faceAux = (pos: Float32Array, owner: Uint8Array | null, sk: Skeleton | null, frame: { center: V3; scale: number } | undefined): Float32Array => {
  const vc = pos.length / 3;
  const aux = new Float32Array(vc * 4);
  const headish = new Set(['head', 'earR', 'earL', 'neck'].map((n) => sk?.byName.get(n)).filter((x): x is number => x !== undefined));
  for (let i = 0; i < vc; i++) {
    aux[i * 4 + 2] = -1; aux[i * 4 + 3] = -9;
    if (!frame) continue;
    const dx = pos[i * 3] - frame.center[0], dy = pos[i * 3 + 1] - frame.center[1], dz = pos[i * 3 + 2] - frame.center[2];
    const onSkull = Math.abs(dx) < frame.scale * 1.65 && dy > -frame.scale * 1.65 && dy < frame.scale * 1.2 && dz > -frame.scale * 0.4;
    if (owner && !headish.has(owner[i]) && !onSkull) continue;
    aux[i * 4] = dx / frame.scale; aux[i * 4 + 1] = dy / frame.scale;
    // Imported socket triangles have normals pointing in every direction.
    // A normal-based mask cuts triangular holes through the drawn eyes. The
    // fitted head's depth is a stable front/back mask, including while posed.
    aux[i * 4 + 2] = Math.min(1, Math.max(-1, (dz / frame.scale + 0.55) * 2)); aux[i * 4 + 3] = dz / frame.scale;
  }
  return aux;
};
/** Texture UV at the vertex closest to a face-space point (front-facing only). */
const uvNear = (aux: Float32Array, uv: Float32Array, u: number, v: number): [number, number] => {
  let best = -1, bd = Infinity;
  for (let i = 0; i < aux.length / 4; i++) {
    if (aux[i * 4 + 2] < 0.45) continue;
    const dd = (aux[i * 4] - u) ** 2 + (aux[i * 4 + 1] - v) ** 2;
    if (dd < bd) { bd = dd; best = i; }
  }
  return best < 0 ? [0.5, 0.5] : [uv[best * 2], uv[best * 2 + 1]];
};

export async function prepareAtelierModel(def: AtelierModelDefinition): Promise<PreparedAtelierModel> {
  const baseId=atelierBaseModel(def.id);
  const lm0 = LANDMARKS[def.id];
  if (!lm0) throw new Error(`Unknown atelier model: ${def.id}`);
  const m = await loadGlb(def.url);
  let vc = m.positions.length / 3;
  // normalise into model space: feet on y = 0, centred, height 1
  let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < vc; i++) {
    const x = m.positions[i * 3], y = m.positions[i * 3 + 1], z = m.positions[i * 3 + 2];
    minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
  }
  const s = 1 / (maxY - minY), cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  let pos: Float32Array = new Float32Array(vc * 3);
  for (let i = 0; i < vc; i++) { pos[i * 3] = (m.positions[i * 3] - cx) * s; pos[i * 3 + 1] = (m.positions[i * 3 + 1] - minY) * s; pos[i * 3 + 2] = (m.positions[i * 3 + 2] - cz) * s; }

  if(baseId!==def.id){
    const baseLm=LANDMARKS[baseId];
    const baseSkeleton=buildSkeleton(baseLm);
    const owners=computeSkin(pos,m.indices,baseSkeleton).owner;
    for(let i=0;i<vc;i++)pos.set(speciesWarp(def.id,baseLm,[pos[i*3],pos[i*3+1],pos[i*3+2]],baseSkeleton.bones[owners[i]].chain==='tail'),i*3);
  }
  const skeleton = buildSkeleton(lm0);
  const skin = computeSkin(pos, m.indices, skeleton);
  let bmp = m.baseColor;
  try {
    if (!bmp) { const c = new OffscreenCanvas(2, 2); const g = c.getContext('2d')!; g.fillStyle = '#c8a070'; g.fillRect(0, 0, 2, 2); bmp = await createImageBitmap(c); }
    // fit the procedural face to the model's own eyes and fill its eye sockets
    let fit: FaceFit | null = null;
    let repair: Float32Array | null = null;
    let whiskers: Uint8Array | null = null;
    let lm = lm0;
    if (lm0?.face && skin && skeleton) {
      const TS = 512;
      const cnv = new OffscreenCanvas(TS, TS); const g2 = cnv.getContext('2d', { willReadFrequently: true })!;
      g2.drawImage(bmp, 0, 0, TS, TS);
      const px = g2.getImageData(0, 0, TS, TS).data;
      const darkness = (i: number): number => {
        const x = Math.min(TS - 1, Math.max(0, Math.floor(m.uvs[i * 2] * TS))), y = Math.min(TS - 1, Math.max(0, Math.floor(m.uvs[i * 2 + 1] * TS)));
        const o = (y * TS + x) * 4;
        return 1 - (0.299 * px[o] + 0.587 * px[o + 1] + 0.114 * px[o + 2]) / 255;
      };
      const headB = new Set(['head', 'earR', 'earL'].map((x) => skeleton.byName.get(x)).filter((x): x is number => x !== undefined));
      fit = fitFace(pos, m.indices, skin.owner, headB, lm0.face, darkness, { preserveMuzzle: baseId === 'fox-st' || baseId === 'hare-st', softenMuzzleRim: baseId === 'tabby-71s', preserveBrowRidge: baseId === 'ink-st' });
      const repaired = textureFacePatches(fit, m.uvs, def.id === 'tabby-71s');
      repair = repaired.repair;
      whiskers = Uint8Array.from(repaired.source, i => fit!.whiskers[i]);
      pos = repaired.pos; m.uvs = repaired.uv; m.indices = repaired.indices; vc = pos.length / 3;
      const joints = new Uint8Array(vc * 4), weights = new Float32Array(vc * 4), owner = new Uint8Array(vc);
      for (let i = 0; i < vc; i++) {
        const src = repaired.source[i];
        joints.set(skin.joints.subarray(src * 4, src * 4 + 4), i * 4);
        weights.set(skin.weights.subarray(src * 4, src * 4 + 4), i * 4); owner[i] = skin.owner[src];
      }
      skin.joints = joints; skin.weights = weights; skin.owner = owner;
      lm = { ...lm0, face: fit.frame };
    }
    const indices = m.indices, smoothN = weldedSmoothNormals(pos, indices);
    const aux = faceAux(pos, skin.owner, skeleton, lm.face);
    for (let i = 0; i < vc; i++) if (whiskers?.[i]) { aux[i * 4 + 2] = -2; aux[i * 4 + 3] = -8; }
    const body: Body = { pos, indices, owner: skin.owner, joints: skin.joints, weights: skin.weights, sk: skeleton, lm };
    const donor: Donor = { pos, nrm: smoothN, uv: m.uvs, indices, lm, aux, repair: repair ?? undefined, owner: skin.owner, joints: skin.joints, weights: skin.weights, sk: skeleton };
    const cheek = baseId === 'fox-st' ? uvNear(aux, m.uvs, 0, .78) : uvNear(aux, m.uvs, .95, -.3);
    // Tabby's upper muzzle sample borders a dark painted socket in the atlas.
    // Its filtered mip turns the erased eye region into a grey band. Sample the
    // continuous lower muzzle instead, without changing the face mask or mesh.
    const muzzle = uvNear(aux, m.uvs, .45, baseId === 'tabby-71s' || baseId === 'tabby-st' ? -.95 : -.65);
    return { modelId: def.id, body, donor, fit, skinUv: [cheek[0], cheek[1], muzzle[0], muzzle[1]], albedo: bmp };
  } catch (error) { bmp?.close(); throw error; }
}
